import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/axiosInstance';
import signalrService from '../services/signalrService';

const MonitoringContext = createContext();

export const MonitoringProvider = ({ children }) => {
    const [servers, setServers] = useState([]);
    const [loadingServers, setLoadingServers] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const connected = connectionStatus === 'connected';
    const [hoPkg, setHoPkg] = useState(null);
    const [dsPkg, setDsPkg] = useState(null);
    const [ssPkg, setSsPkg] = useState(null);
    const [updateLogs, setUpdateLogs] = useState([]);
    const [priceLogs, setPriceLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    const [refreshRate, setRefreshRate] = useState(() => {
        const saved = localStorage.getItem('refreshRate');
        return saved !== null ? Number(saved) : 60000;
    });

    useEffect(() => {
        localStorage.setItem('refreshRate', refreshRate);
    }, [refreshRate]);

    const notify = useCallback((msg) => {
        setToast(msg);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 3000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimer.current), []);

    const fetchServers = useCallback(async () => {
        setServers((prev) => {
            if (!prev || prev.length === 0) setLoadingServers(true);
            return prev;
        });
        try {
            const res = await api.get('/releases/servers');
            const data = Array.isArray(res.data) ? res.data : [];
            setServers((prev) => {
                const prevArray = Array.isArray(prev) ? prev : [];
                if (prevArray.length) {
                    data.forEach((newS) => {
                        const oldS = prevArray.find((s) => s.serverId === newS.serverId);
                        if (oldS && oldS.status?.toLowerCase() === 'online' && newS.status?.toLowerCase() === 'offline') {
                            notify(`Warning "${newS.groupName || oldS.groupName || 'Unknown Group'}" is offline`);
                        }
                    });
                }
                return data;
            });
        } catch {
            notify('Error loading server list.');
        } finally {
            setLoadingServers(false);
        }
    }, [notify]);

    const fetchTargets = useCallback(async () => {
        try {
            const res = await api.get('/releases/targets');
            const data = res.data;
            if (data.targetHo) setHoPkg({ name: 'HeadOfficeV2.zip', size: 0, version: data.targetHo });
            if (data.targetDs) setDsPkg({ name: 'DataServices.zip', size: 0, version: data.targetDs });
            if (data.targetSs) setSsPkg({ name: 'ServerService.zip', size: 0, version: data.targetSs });
        } catch { }
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoadingLogs(true);
        try {
            const [upRes, prRes] = await Promise.all([
                api.get('/releases/logs/updates'),
                api.get('/releases/logs/price-updates'),
            ]);
            setUpdateLogs(upRes.data);
            setPriceLogs(prRes.data);
        } catch (err) {
            console.error('Error loading logs:', err);
        } finally {
            setLoadingLogs(false);
        }
    }, []);

    const fetchServersSilently = useCallback(async () => {
        try {
            const res = await api.get('/releases/servers');
            const data = Array.isArray(res.data) ? res.data : [];
            setServers((prev) => {
                const prevArray = Array.isArray(prev) ? prev : [];
                if (prevArray.length) {
                    data.forEach((newS) => {
                        const oldS = prevArray.find((s) => s.serverId === newS.serverId);
                        if (oldS && oldS.status?.toLowerCase() === 'online' && newS.status?.toLowerCase() === 'offline') {
                            notify(`Warning "${newS.groupName || oldS.groupName || 'Unknown Group'}" is offline`);
                        }
                    });
                }
                return data;
            });
        } catch { }
    }, [notify]);

    const fetchTargetsSilently = useCallback(async () => {
        try {
            const res = await api.get('/releases/targets');
            const data = res.data;
            if (data.targetHo) setHoPkg((p) => ({ name: 'HeadOfficeV2.zip', size: 0, ...p, version: data.targetHo }));
            if (data.targetDs) setDsPkg((p) => ({ name: 'DataServices.zip', size: 0, ...p, version: data.targetDs }));
            if (data.targetSs) setSsPkg((p) => ({ name: 'ServerService.zip', size: 0, ...p, version: data.targetSs }));
        } catch { }
    }, []);

    const fetchLogsSilently = useCallback(async () => {
        try {
            const [upRes, prRes] = await Promise.all([
                api.get('/releases/logs/updates'),
                api.get('/releases/logs/price-updates'),
            ]);
            setUpdateLogs(upRes.data);
            setPriceLogs(prRes.data);
        } catch { }
    }, []);

    const refreshData = useCallback(async () => {
        try {
            await Promise.all([
                fetchServers(),
                fetchTargets(),
                fetchLogs()
            ]);
        } catch (err) {
            console.error('Unified refresh failed:', err);
        }
    }, [fetchServers, fetchTargets, fetchLogs]);

    const handleUpload = useCallback(async (file, software, version, filePath, targetEnv) => {
        const formData = new FormData();
        if (file) formData.append('file', file);
        if (filePath) formData.append('filePath', filePath);
        formData.append('software', software);
        formData.append('version', version);
        if (targetEnv) formData.append('serverType', targetEnv);

        try {
            await api.post('/releases/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const pkgData = {
                name: file ? file.name : filePath?.split(/[/\\]/).pop() || `${software}.zip`,
                size: file ? file.size : 0,
                version,
            };
            if (software === 'HeadOfficeV2') setHoPkg(pkgData);
            else if (software === 'ServerService') setSsPkg(pkgData);
            else setDsPkg(pkgData);
            notify(`${software} ${version} uploaded and targeted.`);
            fetchLogs();
        } catch (err) {
            notify(`Upload failed for ${software}: ${err.message}`);
            throw err;
        }
    }, [notify, fetchLogs]);

    const triggerDeploy = useCallback(async (software, version) => {
        try {
            await api.post('/releases/deploy', { software, version });
            notify(`Deploy signal sent for ${software} (${version}).`);
            fetchLogs();
        } catch (err) {
            notify(`Deploy failed: ${err.message}`);
        }
    }, [notify, fetchLogs]);

    const triggerDeployGroup = useCallback(async (groupName, software, version) => {
        try {
            await api.post('/releases/deploy-group', { groupName, software, version });
            notify(`Group Deploy targeted to group ${groupName} for ${software} ${version}.`);
            fetchLogs();
        } catch (err) {
            notify(`Group deploy failed: ${err.message}`);
        }
    }, [notify, fetchLogs]);

    const triggerRollback = useCallback(async (software, mode) => {
        try {
            await api.post('/releases/rollback', { software, mode });
            notify(`Rollback signal (${mode}) sent for ${software}.`);
            fetchLogs();
        } catch (err) {
            notify(`Rollback failed: ${err.message}`);
        }
    }, [notify, fetchLogs]);

    const triggerServerAction = useCallback(async (serverId, action, versions) => {
        try {
            // `versions` (optional) carries explicit per-server target overrides from the update dialog.
            await api.post('/releases/action', { serverId, action, ...(versions || {}) });
            notify(`${action} triggered on ${serverId}.`);
            fetchLogs();
        } catch (err) {
            notify(`Action failed: ${err.message}`);
        }
    }, [notify, fetchLogs]);

    const triggerUpdateAll = useCallback(async () => {
        try {
            await api.post('/releases/update-all');
            notify('Update all signal triggered for all active servers.');
            fetchLogs();
        } catch (err) {
            notify(`Update all failed: ${err.message}`);
        }
    }, [notify, fetchLogs]);

    useEffect(() => {
        const unsubConnecting = signalrService.registerCallback('onConnecting', () => {
            setConnectionStatus('connecting');
        });

        const unsubConnected = signalrService.registerCallback('onConnected', () => {
            setConnectionStatus('connected');
            fetchServers();
            fetchTargets();
            fetchLogs();
        });

        const unsubDisconnected = signalrService.registerCallback('onDisconnected', () => {
            setConnectionStatus('disconnected');
        });

        const unsubUpdateServerStatus = signalrService.registerCallback('onUpdateServerStatus', (telemetry) => {
            setServers((prev) => {
                const prevArray = Array.isArray(prev) ? prev : [];
                const idx = prevArray.findIndex((s) => s.serverId === telemetry.serverId);
                if (idx >= 0) {
                    const prevServer = prevArray[idx];
                    const wasOnline = prevServer.status?.toLowerCase() === 'online';
                    const isOffline = telemetry.status?.toLowerCase() === 'offline';
                    if (wasOnline && isOffline) {
                        notify(`Warning "${telemetry.groupName || prevServer.groupName || 'Unknown Group'}" is offline`);
                    }
                }

                const row = {
                    serverId: telemetry.serverId,
                    groupName: telemetry.groupName,
                    serverType: telemetry.serverType,
                    serverTypeId: telemetry.serverTypeId,
                    displayName: telemetry.displayName,
                    dbServerId: telemetry.dbServerId,
                    sqlVersion: telemetry.sqlVersion,
                    hoVersion: telemetry.hoVersion,
                    dsVersion: telemetry.dsVersion,
                    ssVersion: telemetry.ssVersion,
                    targetHoVersion: telemetry.targetHoVersion,
                    targetDsVersion: telemetry.targetDsVersion,
                    targetSsVersion: telemetry.targetSsVersion,
                    status: telemetry.status,
                    priceUpdates: telemetry.priceUpdates,
                    ssisInstalled: telemetry.ssisInstalled,
                    lastPriceUpdate: telemetry.lastPriceUpdate,
                    lastHeartbeat: new Date(),
                    isDemo: telemetry.isDemo,
                    lastSoftwareUpdate: telemetry.lastSoftwareUpdate,
                };

                if (idx >= 0) {
                    const next = [...prevArray];
                    next[idx] = row;
                    return next;
                }
                return [...prevArray, row];
            });
        });

        // Initialize and start the centralized SignalR service
        signalrService.start();

        return () => {
            unsubConnecting();
            unsubConnected();
            unsubDisconnected();
            unsubUpdateServerStatus();
            signalrService.stop();
        };
    }, [fetchServers, fetchTargets, fetchLogs, notify]);

    useEffect(() => {
        if (refreshRate <= 0) return;
        const interval = setInterval(() => {
            fetchServersSilently();
            fetchTargetsSilently();
            fetchLogsSilently();
        }, refreshRate);
        return () => clearInterval(interval);
    }, [refreshRate, fetchServersSilently, fetchTargetsSilently, fetchLogsSilently]);

    useEffect(() => {
        fetchServers();
        fetchTargets();
        fetchLogs();
    }, [fetchServers, fetchTargets, fetchLogs]);

    const safeServers = useMemo(() => Array.isArray(servers) ? servers : [], [servers]);

    const onlineCount = useMemo(() => safeServers.filter((s) => s.status?.toLowerCase() === 'online').length, [safeServers]);
    const mismatchesCount = useMemo(
        () =>
            safeServers.filter((s) => {
                const hoMatch = s.hoVersion === s.targetHoVersion;
                const dsMatch = s.dsVersion === s.targetDsVersion;
                const ssMatch = s.ssVersion === s.targetSsVersion;
                return !hoMatch || !dsMatch || !ssMatch;
            }).length,
        [safeServers]
    );

    const hoMismatches = useMemo(() => safeServers.filter(s => s.hoVersion !== s.targetHoVersion).length, [safeServers]);
    const dsMismatches = useMemo(() => safeServers.filter(s => s.dsVersion !== s.targetDsVersion).length, [safeServers]);
    const ssMismatches = useMemo(() => safeServers.filter(s => s.ssVersion !== s.targetSsVersion).length, [safeServers]);

    const runningUpdatesCount = useMemo(
        () =>
            safeServers.filter((s) => {
                if (!s.status) return false;
                const st = s.status.toLowerCase();
                return st !== 'online' && st !== 'offline';
            }).length,
        [safeServers]
    );
    const groups = useMemo(() => [...new Set(safeServers.map((s) => s.groupName).filter(Boolean))], [safeServers]);

    const value = {
        servers,
        loadingServers,
        connectionStatus,
        connected,
        hoPkg,
        dsPkg,
        ssPkg,
        updateLogs,
        priceLogs,
        loadingLogs,
        toast,
        refreshRate,
        setRefreshRate,
        onlineCount,
        mismatchesCount,
        hoMismatches,
        dsMismatches,
        ssMismatches,
        runningUpdatesCount,
        groups,
        fetchServers,
        fetchTargets,
        fetchLogs,
        refreshData,
        handleUpload,
        triggerDeploy,
        triggerDeployGroup,
        triggerRollback,
        triggerServerAction,
        triggerUpdateAll,
        notify,
    };

    return <MonitoringContext.Provider value={value}>{children}</MonitoringContext.Provider>;
};

export const useMonitoring = () => useContext(MonitoringContext);